import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, HardDrive, Check, X, Loader2 } from 'lucide-react';

export default function StorageSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [provider, setProvider] = useState('s3');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [s3Data, setS3Data] = useState({
    bucketName: '',
    region: 'us-east-1',
    accessKeyId: '',
    secretAccessKey: '',
  });

  const [azureData, setAzureData] = useState({
    containerName: '',
    storageAccount: '',
    sasToken: '',
  });

  const [gcpData, setGcpData] = useState({
    bucketName: '',
    projectId: '',
    serviceAccountJson: '',
  });

  useEffect(() => {
    fetchConfig();
  }, [orgId]);

  const fetchConfig = async () => {
    try {
      const response = await apiClient.get(`/storage/orgs/${orgId}/config`);
      setConfig(response.data);
      if (response.data.provider) {
        setProvider(response.data.provider);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const handleSave = async () => {
    try {
      const formData = provider === 's3' ? s3Data : provider === 'azure' ? azureData : gcpData;
      await apiClient.post(`/storage/orgs/${orgId}/configure`, {
        provider,
        ...formData,
      });
      toast.success('Storage configuration saved');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiClient.post(`/storage/orgs/${orgId}/test`);
      setTestResult(response.data);
      if (response.data.status === 'success') {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
      fetchConfig();
    } catch (error) {
      setTestResult({ status: 'failed', message: 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/enterprise/orgs/${orgId}/settings/infrastructure`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Storage Configuration (BYOS)</h1>
          <p className="text-gray-600 mt-1">Bring your own S3, Azure, or GCP storage</p>
        </div>
      </div>

      {config?.configured && (
        <Card className="p-4 rounded-lg">
          <div className="flex items-center gap-3">
            {config.status === 'connected' ? (
              <div className="p-2 bg-success/10 rounded-lg">
                <Check className="w-5 h-5 text-success" />
              </div>
            ) : (
              <div className="p-2 bg-warning/10 rounded-lg">
                <HardDrive className="w-5 h-5 text-warning" />
              </div>
            )}
            <div className="flex-1">
              <p className="font-semibold text-gray-900">
                Provider: {config.provider?.toUpperCase()}
              </p>
              <p className="text-sm text-gray-600">Status: {config.status}</p>
            </div>
            <Button onClick={handleTest} disabled={testing} size="sm">
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-6 rounded-lg space-y-4">
        <div>
          <Label>Storage Provider</Label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="s3">AWS S3</option>
            <option value="azure">Azure Blob Storage</option>
            <option value="gcp">Google Cloud Storage</option>
          </select>
        </div>

        {provider === 's3' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bucket Name *</Label>
                <Input
                  value={s3Data.bucketName}
                  onChange={(e) => setS3Data({ ...s3Data, bucketName: e.target.value })}
                  placeholder="my-humatwin-bucket"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label>Region *</Label>
                <Input
                  value={s3Data.region}
                  onChange={(e) => setS3Data({ ...s3Data, region: e.target.value })}
                  placeholder="us-east-1"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>
            <div>
              <Label>Access Key ID *</Label>
              <Input
                value={s3Data.accessKeyId}
                onChange={(e) => setS3Data({ ...s3Data, accessKeyId: e.target.value })}
                placeholder="AKIAIOSFODNN7EXAMPLE"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Secret Access Key *</Label>
              <Input
                type="password"
                value={s3Data.secretAccessKey}
                onChange={(e) => setS3Data({ ...s3Data, secretAccessKey: e.target.value })}
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                className="mt-1 font-mono text-sm"
              />
            </div>
          </>
        )}

        {provider === 'azure' && (
          <>
            <div>
              <Label>Container Name *</Label>
              <Input
                value={azureData.containerName}
                onChange={(e) => setAzureData({ ...azureData, containerName: e.target.value })}
                placeholder="humatwin-files"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Storage Account Name *</Label>
              <Input
                value={azureData.storageAccount}
                onChange={(e) => setAzureData({ ...azureData, storageAccount: e.target.value })}
                placeholder="mystorageaccount"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>SAS Token *</Label>
              <Input
                type="password"
                value={azureData.sasToken}
                onChange={(e) => setAzureData({ ...azureData, sasToken: e.target.value })}
                placeholder="?sv=2021-01-01&ss=b&srt=sco..."
                className="mt-1 font-mono text-sm"
              />
            </div>
          </>
        )}

        {provider === 'gcp' && (
          <>
            <div>
              <Label>Bucket Name *</Label>
              <Input
                value={gcpData.bucketName}
                onChange={(e) => setGcpData({ ...gcpData, bucketName: e.target.value })}
                placeholder="my-humatwin-bucket"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Project ID *</Label>
              <Input
                value={gcpData.projectId}
                onChange={(e) => setGcpData({ ...gcpData, projectId: e.target.value })}
                placeholder="my-project-123456"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Service Account JSON *</Label>
              <Textarea
                value={gcpData.serviceAccountJson}
                onChange={(e) => setGcpData({ ...gcpData, serviceAccountJson: e.target.value })}
                placeholder='{"type": "service_account", "project_id": "...", ...}'
                rows={5}
                className="mt-1 font-mono text-xs"
              />
            </div>
          </>
        )}

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave}>
            Save Configuration
          </Button>
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || !config?.configured}
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
        </div>

        {testResult && (
          <div className={`p-4 rounded-lg ${
            testResult.status === 'success'
              ? 'bg-success/10 border border-success/20'
              : 'bg-error/10 border border-error/20'
          }`}>
            <p className={`text-sm font-medium ${
              testResult.status === 'success' ? 'text-success' : 'text-error'
            }`}>
              {testResult.message}
            </p>
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-lg bg-blue-50 border-blue-200">
        <h4 className="font-semibold text-gray-900 mb-2">Important Notes</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Credentials are encrypted at rest using Fernet</li>
          <li>• Storage is used for Vault uploads if configured</li>
          <li>• GridFS is used as fallback if BYOS is not configured</li>
          <li>• Ensure bucket/container has proper permissions for HumaTwin</li>
        </ul>
      </Card>
    </div>
  );
}
